import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { PageTemplate, PageBody, PageFooter } from "../shared-components";
import { SkeletonLoader, SummaryButton } from "./components";
import { KlarnaEmailInput } from "./components";
import { useOutletContext, useNavigate } from "react-router-dom";
import { AdyenPaymentBnpl, Modal } from "@components";
import { cn } from "@utils";
import { usePostHog } from "posthog-js/react";
import { useAdyenPageReload } from "../../hooks";

const PRPayByBnpl = () => {
  const {
    id,
    paymentRequestId,
    checkoutId,
    // core payment fields from outlet context
    merchant,
    amount,
    amountRequested,
    customerName,
    customerIsFinanced,
    customerId,
    branchId,
    salesOrderIds,
    paymentType,
    status,
    recipientEmail,
    source,
    transactionIds,
    reference,
    // card/BNPL flags
    klarnaPaymentAllowed,
    cardPaymentProvider,
    // signed tokens for payment integrity validation
    paymentRequestToken,
    checkoutToken,
    // other flow/context
    statement,
    processingUrl,
    successUrl,
    summaryData,
    settlementDiscount,
    paymentDiscounts,
    setMethodStepData,
    methodStepData,
    flowId,
  } = useOutletContext();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [currentAmount, setCurrentAmount] = useState(null);
  const [adyenLoading, setAdyenLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [sessionExpired, setSessionExpired] = useState(false);
  const [klarnaEmailError, setKlarnaEmailError] = useState("");

  const [klarnaEmailAddress, setKlarnaEmailAddress] = useState("");
  const [klarnaEmailAddressConfirmed, setKlarnaEmailAddressConfirmed] =
    useState(false);

  const bnplPaymentParameters = useMemo(
    () => ({
      // identifiers
      id,
      paymentRequestId,
      checkoutId,
      merchantId: merchant?.id,

      // monetary
      amount,
      amountRequested,

      // customer context
      customerName,
      customerId,
      customerIsFinanced,
      branchId,
      customerEmail: recipientEmail,

      // sales / reference
      salesOrderIds,
      paymentType,
      reference,

      // meta
      source,
      transactionIds,
      paymentDiscounts,

      // signed token forwarded unchanged to BFF for validation
      paymentRequestToken,
      checkoutToken,
    }),
    [
      id,
      paymentRequestId,
      checkoutId,
      merchant,
      amount,
      amountRequested,
      customerName,
      customerId,
      customerIsFinanced,
      branchId,
      recipientEmail,
      salesOrderIds,
      paymentType,
      reference,
      source,
      transactionIds,
      paymentDiscounts,
      paymentRequestToken,
      checkoutToken,
    ]
  );

  const adyenRef = useRef(null);
  const posthog = usePostHog();

  // Coordinate one-time reload behaviour with the card page so Adyen
  // components reset correctly when navigating between Klarna and card.
  useAdyenPageReload({
    visitedKey: "AdyenBnplPageVisited",
    reloadOnKey: "AdyenCardPageVisited",
  });

  useEffect(() => {
    if (!posthog) return;

    posthog.capture("klarna_page_viewed", {
      paymentRequestId: id,
      merchantId: merchant?.id,
      merchantName: merchant?.name,
      amount,
    });
  }, [posthog, id, amount, merchant]);

  const handleKlarnaNotAllowed = () => {
    console.error("Klarna payments are not allowed for this payment request");
    navigate(`/${flowId}/${id}/paymentmethod`);
  };

  useEffect(() => {
    if (amount) {
      setCurrentAmount({ baseAmount: amount });
    }
  }, [amount]);

  useEffect(() => {
    if (recipientEmail) {
      setKlarnaEmailAddress(recipientEmail);
    }
  }, [recipientEmail]);

  useEffect(() => {
    if (!amount || Number(amount) === 0) {
      navigate(`/${flowId}/${id}/`);
      return;
    }

    if (!klarnaPaymentAllowed || cardPaymentProvider !== "Adyen") {
      handleKlarnaNotAllowed();
    }
  }, [amount, id, klarnaPaymentAllowed, flowId, navigate, cardPaymentProvider]);

  const validateEmailAndConfirm = () => {
    const isValidEmail =
      typeof klarnaEmailAddress === "string" &&
      /^\S+@\S+\.\S+$/.test(klarnaEmailAddress.trim());

    if (!isValidEmail) {
      setKlarnaEmailError("Please enter a valid email address");
      return false;
    }

    setKlarnaEmailError("");
    setErrorMessage("");
    setKlarnaEmailAddressConfirmed(true);
    return true;
  };

  const handleContinue = () => {
    if (!klarnaPaymentAllowed) {
      handleKlarnaNotAllowed();
      return;
    }

    if (!klarnaEmailAddressConfirmed) {
      const confirmed = validateEmailAndConfirm();
      if (!confirmed) {
        return;
      }
      return;
    }

    // Store payment details for success page
    setMethodStepData({
      ...methodStepData,
      method: "paybybnpl",
      finalAmount: currentAmount,
      paidWithKlarna: true,
    });

    if (cardPaymentProvider === "Adyen") {
      console.log("submitPayment");
      adyenRef.current?.submitPayment();
    } else {
      console.error("Unsupported payment provider: " + cardPaymentProvider);
    }
  };

  const onBnplError = (err) => {
    if (err?.tokenExpired) { setSessionExpired(true); return; }
    setProcessing(false);
  };

  const handleAmountChange = useCallback((amountObj) => {
    if (amountObj && typeof amountObj === "object") {
      setCurrentAmount(amountObj);
    }
  }, []);

  const disabled = useMemo(() => {
    return (
      !klarnaEmailAddressConfirmed ||
      ((processing || adyenLoading || !!errorMessage) &&
        klarnaEmailAddressConfirmed)
    );
  }, [processing, adyenLoading, errorMessage, klarnaEmailAddressConfirmed]);

  const merchantColourSet = useMemo(() => {
    return merchant?.colourSet;
  }, [merchant]);

  return (
    <>
    <Modal isOpen={sessionExpired} onClose={() => navigate(`/${flowId}/${id}/`)} title="Session expired">
      <div className="p-5 flex flex-col gap-4">
        <p className="text-sm text-zinc-700">Payment session expired. Please start again.</p>
        <button
          onClick={() => navigate(`/${flowId}/${id}/`)}
          className="w-full rounded-lg bg-zinc-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-zinc-700 cursor-pointer"
        >
          OK
        </button>
      </div>
    </Modal>
    <PageTemplate>
      <PageBody innerClassName="flex flex-col gap-4">
        <div className="px-5 lg:px-0">
          <h3 className="text-xl/8 font-semibold">
            Enter an email to use with Klarna
          </h3>
          <h4 className="text-sm text-zinc-500">
            You will need to login / signup with this email
          </h4>
        </div>
        <div className="px-5 lg:px-0 overflow-x-visible flex flex-col grow">
          <KlarnaEmailInput
            klarnaEmailAddress={klarnaEmailAddress}
            setKlarnaEmailAddress={setKlarnaEmailAddress}
            klarnaEmailError={klarnaEmailError}
            setKlarnaEmailError={setKlarnaEmailError}
            klarnaEmailAddressConfirmed={klarnaEmailAddressConfirmed}
            setKlarnaEmailAddressConfirmed={setKlarnaEmailAddressConfirmed}
            validateEmailAndConfirm={validateEmailAndConfirm}
            merchant={merchant}
            merchantColourSet={merchantColourSet}
          />

          {klarnaEmailAddressConfirmed && (
            <div className="grid mt-4 pt-5 px-4 bg-zinc-100 rounded-xl border border-zinc-300 relative overflow-hidden">
              <div className="row-start-1 col-start-1">
                <AdyenPaymentBnpl
                  paymentParameters={bnplPaymentParameters}
                  processingUrl={processingUrl}
                  successUrl={successUrl}
                  onAmountChange={handleAmountChange}
                  onError={onBnplError}
                  setAdyenLoading={setAdyenLoading}
                  setProcessing={setProcessing}
                  externalSubmitRef={adyenRef}
                  klarnaEmailAddress={klarnaEmailAddress}
                  klarnaEmailAddressConfirmed={klarnaEmailAddressConfirmed}
                />
              </div>
              <SkeletonLoader show={adyenLoading} />
            </div>
          )}
        </div>
      </PageBody>

      <PageFooter
        className={cn(
          "pt-4",
          !klarnaEmailAddressConfirmed && "hidden lg:block"
        )}
      >
        <div className="pb-7 space-y-2 hidden lg:block">
          <h2 className="text-xl font-medium">Payment summary</h2>
        </div>
        <SummaryButton
          errorMessage={errorMessage}
          currentAmount={currentAmount}
          handleContinue={handleContinue}
          processing={processing}
          adyenLoading={adyenLoading}
          klarnaEmailAddressConfirmed={klarnaEmailAddressConfirmed}
          disabled={disabled}
          merchant={merchant}
          hasStatement={!!statement}
          summaryData={summaryData}
          settlementDiscount={settlementDiscount}
        />
      </PageFooter>
    </PageTemplate>
    </>
  );
};

export default PRPayByBnpl;
